import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useSearch } from '../../hooks/useSearch';
import SearchSuggestions from '../common/SearchSuggestions';
import { Tool } from '../../types';
import { icons } from '../../data/icons';
import styles from '../../styles/modules/Header.module.css';

const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { searchQuery, setSearchQuery } = useSearch();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setShowSuggestions(true);
  };

  const handleSearchFocus = () => {
    if (searchQuery.trim()) {
      setShowSuggestions(true);
    }
  };

  const handleSuggestionSelect = (_tool: Tool) => {
    setSearchQuery('');
    setShowSuggestions(false);
  };

  const handleCloseSuggestions = () => {
    setShowSuggestions(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className={styles.header}>
      <div className="container">
        <div className={styles.headerContent}>
          <div className={styles.leftSection}>
            <a 
              href="https://lab39.org" 
              className={styles.lab39Link}
              title="lab39"
            >
              <span className={styles.lab39Text}>lab39</span>
              <span className={styles.lab39Dot}>.</span>
            </a>
            <span className={styles.separator}>|</span>
            <Link to="/" className={styles.logo}>
              <span className={styles.logoText}>devTools</span>
              <span className={styles.logoDot}>.</span>
            </Link>
          </div>

          <div className={styles.searchContainer} ref={searchContainerRef}>
            <div className={styles.searchIcon}>{icons.search}</div>
            <input
              type="text"
              placeholder="Search tools..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              className={styles.searchInput}
            />
            {showSuggestions && (
              <SearchSuggestions
                searchQuery={searchQuery}
                onSuggestionSelect={handleSuggestionSelect}
                onClose={handleCloseSuggestions}
              />
            )}
          </div>

          <div className={styles.actions}>
            <button
              onClick={toggleTheme}
              className={styles.themeToggle}
              aria-label="Toggle theme"
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              <span className={styles.iconWrapper}>
                {theme === 'light' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="5" />
                    <line x1="12" y1="1" x2="12" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" />
                    <line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                  </svg>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;